// @ts-strict-ignore
import {
  selectFocusedAction,
  selectPopulatedThresholdById,
  selectReversedLegacyActionMappings,
  selectThresholdById,
  selectWorkflowData,
  selectWorkflowProps,
} from './general';
import type { State } from '~/store';
import { getIsChampionChallengerEnabled } from '../../../service/testingSuite';
import type { EvidencesTableProps } from '~/entities/evaluations/TagRuleModal/EvidencesTable';
import { createSelector } from 'reselect';
import type { Action } from 'flow/store/engine/types';
import { selectInitialPopulatedThresholdById, selectInitialThresholdById } from './history';
import { selectThresholdSimParamsByPublicId } from '~/flow/thresholdSimulator/redux/selectors';
import { getThresholdSimDisabledReason, getThresholdType } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/ThresholdSimulatorModal/helpers';


export function selectIsChampionChallengerEnabled(state: State) {
  const workflowProps = selectWorkflowProps(state);
  return getIsChampionChallengerEnabled({
    championChallengerStarted: workflowProps?.workflow?.champion_challenger_started,
    championChallengerEnded: workflowProps?.workflow?.champion_challenger_ended,
  });
}

export function selectThresholdOutputResults(state: State) {
  return state.workflowEditor.workflowFeatures.ruleExplainability.thresholdResults;
}

export function selectIsRuleExplainabilityPortfolioEvaluation(state: State) {
  return state.workflowEditor.workflowFeatures.ruleExplainability.isPortfolioEvaluation;
}

export function selectShouldAutoLayoutOutcomes(state: State) {
  // currently only renders as node under the rule explainability mode
  return selectFocusedAction(state) !== null && selectThresholdOutputResults(state) !== null;
}

// ================ Rule Explainability ================

export function selectEntityToken(state: State) {
  return state.workflowEditor.workflowFeatures.ruleExplainability.entityToken;
}

export function selectEvaluationToken(state: State) {
  return state.workflowEditor.workflowFeatures.ruleExplainability.evaluationToken;
}

export function selectDimensions(state: State) {
  return state.workflowEditor.workflowFeatures.ruleExplainability.dimensions;
}

export function selectSelectedEvidenceThresholdId(state: State) {
  return state.workflowEditor.workflowFeatures.ruleExplainability.thresholdId;
}

export function selectEvaluationOutcome(state: State) {
  return state.workflowEditor.workflowFeatures.ruleExplainability.setOutcome;
}

export function selectMatrixModelResults(state: State) {
  return state.workflowEditor.workflowFeatures.ruleExplainability.matrixModelResults;
}

/**
 * Returns props for evidence table if it is supported.
 * Returns null if the table is not supported.
*/
export const selectEvidencesTableProps = createSelector(
  [
    (state, sourceAction: Action) => sourceAction,
    selectDimensions,
    selectEntityToken,
    selectEvaluationToken,
    selectReversedLegacyActionMappings,
    selectSelectedEvidenceThresholdId,
  ],
  (
    sourceAction,
    dimensions,
    entityToken,
    evaluationToken,
    reversedLegacyMappings,
    thresholdId,
  ): EvidencesTableProps | null => {
    const legacyActionId = reversedLegacyMappings[sourceAction.publicId];

    const requiredConditions = new Set([
      Boolean(entityToken),
      Boolean(evaluationToken),
      Boolean(legacyActionId),
      Boolean(thresholdId),
    ]);

    // all conditions must be true for evidence table to be supported
    return requiredConditions.has(false)
      ? null
      : {
        actionId: legacyActionId,
        dimensions,
        entityToken,
        evaluationToken,
        thresholdId,
        isPortfolioEvaluation: false,
      };
  },
);

// ================ Threshold Simulator ================

export function selectSelectedThresholdSimThresholdId(state: State) {
  return state.workflowEditor.workflowFeatures.thresholdSimulator.thresholdId;
}

export function selectSelectedThresholdSimThreshold(state: State) {
  const thresholdId = selectSelectedThresholdSimThresholdId(state);
  return thresholdId ? selectThresholdById(thresholdId)(state) : null;
}

export function selectSelectedThresholdSimInitialThreshold(state: State) {
  const thresholdId = selectSelectedThresholdSimThresholdId(state);
  return thresholdId ? selectInitialThresholdById(thresholdId)(state) : null;
}

export function selectThresholdSimPopulatedThreshold(state: State) {
  const thresholdId = selectSelectedThresholdSimThresholdId(state);
  return selectPopulatedThresholdById(thresholdId)(state);
}

export function selectThresholdSimInitialPopulatedThreshold(state: State) {
  const thresholdId = selectSelectedThresholdSimThresholdId(state);
  return selectInitialPopulatedThresholdById(thresholdId)(state);
}

export function selectThresholdSimParams(state: State) {
  const thresholdId = selectSelectedThresholdSimThresholdId(state);
  return selectThresholdSimParamsByPublicId(thresholdId)(state);
}

export function selectThresholdSimType(state: State) {
  const initialPopulatedThreshold = selectThresholdSimInitialPopulatedThreshold(state);
  const workflowData = selectWorkflowData(state);
  const workflowProps = selectWorkflowProps(state);

  return getThresholdType(initialPopulatedThreshold, workflowData, workflowProps);
}

export function selectThresholdSimDisabledReason(thresholdId: string) {
  return function selector(state: State) {
    const threshold = selectPopulatedThresholdById(thresholdId)(state);
    const initialThreshold = selectInitialPopulatedThresholdById(thresholdId)(state);
    const thresholdSimParams = selectThresholdSimParamsByPublicId(thresholdId)(state);

    return getThresholdSimDisabledReason(threshold, initialThreshold, thresholdSimParams);
  };
}
